import { useEffect, useRef } from 'react';
import App from 'next/app';

import { FocusModality } from '@playbooks/ui/components/focus-modality';
import { PageHead } from '@playbooks/ui/components/page-head';

import 'styles/_base.css';
import 'initializers/icons';
import { BaseAdapter } from 'api';
import {
	AppProvider,
	ClientProvider,
	FathomProvider,
	IntercomProvider,
	LoadingProvider,
	PageContext,
	PageProvider,
	SearchProvider,
	SessionProvider,
	StorageProvider,
	StoreProvider,
	ThemeProvider,
	ToastProvider,
	WorkspaceProvider,
} from 'contexts';
import { seo } from 'initializers';
import { sessionQuery } from 'queries/session-queries';
import { logger } from 'utils';

const client = new BaseAdapter({ domain: process.env.NEXT_PUBLIC_API_DOMAIN });

const PlaybooksBlog = ({ Component, pageProps, session }) => {
	const sessionRef = useRef(session);
	if (session) sessionRef.current = session;
	const ssr = { ...pageProps, session: session || sessionRef.current };
	const getLayout = Component.getLayout || (page => page);

	// Hooks
	useEffect(() => {
		logger.info('env: ', process.env.NEXT_PUBLIC_ENV);
		logger.info('ssr: ', ssr);
	}, [ssr]);

	return (
		<>
			<FocusModality />
			<PageHead seo={seo} />
			<ToastProvider>
				<StorageProvider>
					<StoreProvider client={client}>
						<SessionProvider ssr={ssr}>
							<ClientProvider client='blog'>
								<WorkspaceProvider ssr={ssr}>
									<ThemeProvider>
										<IntercomProvider>
											<LoadingProvider>
												<FathomProvider>
													<SearchProvider>
														<AppProvider ssr={ssr}>
															<PageProvider ssr={ssr}>
																<PageContext.Consumer>
																	{contexts => getLayout(<Component ssr={ssr} {...contexts} />, ssr)}
																</PageContext.Consumer>
															</PageProvider>
														</AppProvider>
													</SearchProvider>
												</FathomProvider>
											</LoadingProvider>
										</IntercomProvider>
									</ThemeProvider>
								</WorkspaceProvider>
							</ClientProvider>
						</SessionProvider>
					</StoreProvider>
				</StorageProvider>
			</ToastProvider>
		</>
	);
};

PlaybooksBlog.getInitialProps = async appContext => {
	const appProps = await App.getInitialProps(appContext);
	const { req, params, query } = appContext.ctx;
	try {
		if (!req.cookies.token) return appProps;
		const session = await sessionQuery({ req, params, query });
		return { ...appProps, session };
	} catch (e) {
		return appProps;
	}
};

export default PlaybooksBlog;
